import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Radio } from '@bedrock/components';
import { DataSourceTag, FormCard } from '@purchase/supplier';
import { SupplierAwardsSection } from './SupplierAwardsSection';
import { DigitalingSection } from './DigitalingSection';
import { XpcSection } from './XpcSection';
import { QccSection } from './QccSection';
import { isNil } from 'lodash-es';

const SOURCE = {
  SUPPLIER: 1,
  DIGITALING: 2,
  XPC: 3,
  QCC: 4,
} as const;

type AwardsSource = (typeof SOURCE)[keyof typeof SOURCE];

export interface IndustryAwardsProps {
  isExternal?: boolean;
  formDetail?: any;
  vendorId: string;
}

export const IndustryAwards: FC<IndustryAwardsProps> = ({
  isExternal = false,
  formDetail,
  vendorId,
}) => {
  const supplierAwardsCount =
    formDetail?.extendAttributeInfo?.industryAwards?.length ?? 0;
  const qccRankingCount = formDetail?.vendorQccBangDanInfos?.length ?? 0;
  const qccHonorCount = formDetail?.vendorQccHonorQualifications?.length ?? 0;
  const thirdPartyCount = qccRankingCount + qccHonorCount;
  const qccHonorsUpdateTime = formDetail?.qccDataFetchedAt;

  // 默认 tab 跟着数据走：供应商录入 → 企查查 → 兜底供应商录入。
  // 数英 / 新片场为异步拉取,这里拿不到 count,不参与默认选择。
  const initialAwardsSource = useMemo<AwardsSource>(() => {
    if (supplierAwardsCount > 0) return SOURCE.SUPPLIER;
    if (thirdPartyCount > 0) return SOURCE.QCC;
    return SOURCE.SUPPLIER;
  }, [supplierAwardsCount, thirdPartyCount]);
  const [value, setValue] = useState<AwardsSource>(initialAwardsSource);
  const hasInitFromData = useRef(false);

  useEffect(() => {
    if (hasInitFromData.current) return;
    if (supplierAwardsCount === 0 && thirdPartyCount === 0) return;
    hasInitFromData.current = true;
    setValue(initialAwardsSource);
  }, [initialAwardsSource, supplierAwardsCount, thirdPartyCount]);

  const options = [
    { name: '供应商录入', value: SOURCE.SUPPLIER, count: supplierAwardsCount },
    { name: '数英', value: SOURCE.DIGITALING },
    { name: '新片场', value: SOURCE.XPC },
    { name: '企查查获取', value: SOURCE.QCC, count: thirdPartyCount },
  ];

  // 外包端不展示 Radio,直接看供应商录入数据。
  if (isExternal) {
    return (
      <FormCard title="荣誉奖项">
        <SupplierAwardsSection />
      </FormCard>
    );
  }

  return (
    <FormCard title="荣誉奖项">
      <div className="flex items-center justify-between mb-3">
        <Radio.Group value={value} onChange={(e) => setValue(e.target.value)}>
          {options.map((option) => {
            return (
              <Radio.Button
                checked={option.value === value}
                key={option.value}
                value={option.value}
              >
                <div className="inline-flex items-center gap-1">
                  <span>
                    {option.name}
                    {!isNil(option.count) && ` (${option.count})`}
                  </span>
                </div>
              </Radio.Button>
            );
          })}
        </Radio.Group>
        {value === SOURCE.QCC && (
          <DataSourceTag source="企查查" updateTime={qccHonorsUpdateTime} />
        )}
      </div>

      {value === SOURCE.SUPPLIER && <SupplierAwardsSection />}
      {value === SOURCE.DIGITALING && <DigitalingSection vendorId={vendorId} />}
      {value === SOURCE.XPC && <XpcSection vendorId={vendorId} />}
      {value === SOURCE.QCC && <QccSection formDetail={formDetail} />}
    </FormCard>
  );
};
